import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "../api";
import { MapPin, LogIn, LogOut, Clock, Coffee, Activity, Wifi } from "lucide-react";

const RADIUS_METERS = 100;

export default function EmployeeDashboard() {
  const [loading, setLoading] = useState(true);
  const [att, setAtt] = useState(null);
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [distance, setDistance] = useState(null);
  const [geoError, setGeoError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const [history, setHistory] = useState([]);

  // ✅ NEW: admin/public IP from backend (no CORS issue)
  const [clientIp, setClientIp] = useState("");
  const [ipError, setIpError] = useState("");

  const LIMIT = 10;
  const today = new Date();
  const todayDate = today.toISOString().slice(0, 10);
  const todayLabel = today.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  // ✅ Get Geo Location
  const getLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError("Geo location not supported in this browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoError("");
      },
      () => {
        setGeoError("Please allow location permission to punch attendance.");
      }
    );
  }, []);

  // ✅ NEW: fetch IP from backend (you should create endpoint /auth/my-ip)
  const fetchClientIp = useCallback(async () => {
    try {
      setIpError("");
      const res = await api.get("/organizations/my-ip"); // ✅ backend should return { ip: "x.x.x.x" }
      setClientIp(res?.data?.ip || "");
    } catch (err) {
      console.error("IP FETCH ERROR:", err);
      setClientIp("");
      setIpError("Unable to detect IP. Try again.");
    }
  }, []);

  // ✅ Load last 10 history records
  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get(`/attendance/history?limit=${LIMIT}&page=1`);
      setHistory(res.data.slice(0, LIMIT));
    } catch (err) {
      console.error("HISTORY LOAD ERROR:", err);
    }
  }, []);

  // ✅ Initial Load Data (User & Today's Stats)
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [me, todayAtt, orgRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/attendance/today"),
        api.get("/organizations/me"),
      ]);

      setUser(me.data);
      setAtt(todayAtt.data);
      setOrg(orgRes.data);
    } catch (err) {
      console.error("LOAD ERROR:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Run initial data and location once
  useEffect(() => {
    loadInitialData();
    getLocation();
    loadHistory();
    fetchClientIp(); // ✅ NEW
  }, [loadInitialData, getLocation, loadHistory, fetchClientIp]);

  // Haversine Distance Calculation
  const getDistanceFromLatLonInMeters = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  function getDeviceId() {
    let id = localStorage.getItem("device_id");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("device_id", id);
    }
    return id;
  }

  useEffect(() => {
    if (coords.lat && coords.lng && org?.office_lat && org?.office_lng) {
      const d = getDistanceFromLatLonInMeters(
        Number(org.office_lat),
        Number(org.office_lng),
        coords.lat,
        coords.lng
      );
      setDistance(Math.round(d));
    }
  }, [coords, org]);

  const insideRadius = useMemo(() => {
    return distance != null && distance <= RADIUS_METERS;
  }, [distance]);

  // ✅ NEW: allowed_ips check (if org.allowed_ips exists)
  const parseAllowedIps = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.map(String);

    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch (e) {}

    return String(value)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const allowedIpsList = useMemo(() => {
    return parseAllowedIps(org?.allowed_ips);
  }, [org]);

  const ipAllowed = useMemo(() => {
    if (!clientIp) return false;
    return allowedIpsList.includes(clientIp);
  }, [allowedIpsList, clientIp]);

  const canPunch = useMemo(() => {
    // ✅ GPS OR IP
    return insideRadius || ipAllowed;
  }, [insideRadius, ipAllowed]);

  // Helpers
  const formatTime = (val) => {
    if (!val) return "--";
    const d = new Date(val);
    return isNaN(d.getTime())
      ? "--"
      : d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  };

  const formatDuration = (minutes) => {
    const m = minutes || 0;
    const h = Math.floor(m / 60);
    const r = m % 60;
    return h <= 0 ? `${r} min` : `${h}h ${r}m`;
  };

  const todayStatus = useMemo(() => {
    if (!att || !att?.check_in) return "Not started";
    if (att.check_in && !att.check_out) return "Working";
    return "Completed";
  }, [att]);

  const statusChipClass =
    todayStatus === "Not started"
      ? "bg-slate-100 text-slate-700 border-slate-200"
      : todayStatus === "Working"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : "bg-indigo-50 text-indigo-700 border-indigo-200";

  const showError = (text) => setActionMsg({ type: "error", text });
  const showSuccess = (text) => setActionMsg({ type: "success", text });

  // ✅ UPDATED: allow punch if (GPS inside) OR (IP allowed)
  const checkBeforePunch = () => {
    if (canPunch) return true;

    // If GPS not detected and IP not allowed
    if ((!coords.lat || !coords.lng) && !ipAllowed) {
      showError("Location not detected and IP is not allowed. Please enable GPS or connect to office network.");
      return false;
    }

    // If GPS is outside and IP not allowed
    if (!insideRadius && !ipAllowed) {
      showError(`You are outside the allowed ${RADIUS_METERS}m radius and IP is not allowed.`);
      return false;
    }

    return true;
  };

  const refreshData = async () => {
    const todayAtt = await api.get("/attendance/today");
    setAtt(todayAtt.data);
    loadHistory();
  };

  const checkIn = async () => {
    if (!checkBeforePunch()) return;
    try {
      setActionLoading(true);
      setActionMsg(null);

      const res = await api.post("/attendance/checkin", {
        // ✅ send lat/lng if available, otherwise null (backend now supports GPS OR IP)
        lat: coords.lat || null,
        lng: coords.lng || null,
        device_id: getDeviceId(),
      });

      showSuccess(res?.data?.message || "Check-in successful.");
      await refreshData();
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to check in.");
    } finally {
      setActionLoading(false);
    }
  };

  const checkOut = async () => {
    if (!checkBeforePunch()) return;
    try {
      setActionLoading(true);
      setActionMsg(null);

      const res = await api.post("/attendance/checkout", {
        lat: coords.lat || null,
        lng: coords.lng || null,
        device_id: getDeviceId(),
      });

      showSuccess(res?.data?.message || "Check-out successful.");
      await refreshData();
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to check out.");
    } finally {
      setActionLoading(false);
    }
  };

  const breakStart = async () => {
    try {
      setActionLoading(true);
      setActionMsg(null);
      const res = await api.post("/attendance/break-start");
      showSuccess(res?.data?.message || "Break started.");
      await refreshData();
    } catch (err) {
      showError("Failed to start break.");
    } finally {
      setActionLoading(false);
    }
  };

  const breakEnd = async () => {
    try {
      setActionLoading(true);
      setActionMsg(null);
      const res = await api.post("/attendance/break-end");
      showSuccess(res?.data?.message || "Break ended.");
      await refreshData();
    } catch (err) {
      showError("Failed to end break.");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading)
    return (
      <div className="p-10 text-center text-slate-500 font-medium">
        Loading dashboard...
      </div>
    );

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400">
            Today · {todayDate}
          </p>
          <h1 className="text-2xl font-bold text-slate-900">
            Welcome, {user?.name}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{todayLabel}</p>
        </div>
        <div className="flex flex-col items-start md:items-end gap-2">
          <span
            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-medium ${statusChipClass}`}
          >
            <span className="h-2 w-2 rounded-full bg-current" />
            <span>Status: {todayStatus}</span>
          </span>

          {distance != null ? (
            <p className="text-[11px] text-slate-500">
              You are{" "}
              <span className="font-semibold text-slate-900">{distance}m</span>{" "}
              from office.
            </p>
          ) : geoError ? (
            <p className="text-[11px] text-rose-500">{geoError}</p>
          ) : (
            <p className="text-[11px] text-slate-400">Fetching location...</p>
          )}
        </div>
      </div>

      {/* Action message */}
      {actionMsg && (
        <div
          className={`flex items-start gap-3 p-3 mb-4 rounded-lg text-sm border shadow-sm ${
            actionMsg.type === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-rose-50 border-rose-200 text-rose-700"
          }`}
        >
          <span>{actionMsg.type === "success" ? "✅" : "⚠️"}</span>
          <div>{actionMsg.text}</div>
        </div>
      )}

      {/* Location + IP Card */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <MapPin size={18} className="text-indigo-500" /> Current Location
          </h3>
          <button
            onClick={getLocation}
            className="px-3 py-1 rounded-full border border-slate-200 text-[11px] hover:bg-slate-50 flex items-center gap-1"
          >
            <Activity size={12} /> Refresh
          </button>
        </div>
        <p className="text-sm font-mono">
          {coords.lat?.toFixed(6) || "--"}, {coords.lng?.toFixed(6) || "--"}
        </p>
        {distance != null && (
          <div className="mt-2 flex gap-2 items-center flex-wrap">
            <span
              className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                insideRadius
                  ? "bg-emerald-100 text-emerald-800"
                  : "bg-amber-100 text-amber-800"
              }`}
            >
              {insideRadius ? "IN RADIUS" : "OUTSIDE RADIUS"}
            </span>

            <span
              className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                ipAllowed
                  ? "bg-emerald-100 text-emerald-800"
                  : "bg-slate-100 text-slate-700"
              }`}
              title="Office network IP check"
            >
              {ipAllowed ? "OFFICE IP OK" : "OFFICE IP NO"}
            </span>
          </div>
        )}

        {/* ✅ NEW: Show client IP */}
        <div className="mt-4 pt-3 border-t border-slate-100">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <Wifi size={16} className="text-indigo-500" /> Your Network IP
            </h4>
            <button
              onClick={fetchClientIp}
              className="px-3 py-1 rounded-full border border-slate-200 text-[11px] hover:bg-slate-50 flex items-center gap-1"
            >
              <Activity size={12} /> Refresh IP
            </button>
          </div>

          <p className="text-sm font-mono mt-2">
            {clientIp || "--"}
          </p>
          {ipError && <p className="text-[11px] text-rose-500 mt-1">{ipError}</p>}

          <p className="text-[11px] text-slate-500 mt-2">
            Attendance punch allowed if you are within GPS radius OR connected to an allowed office IP.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard title="Check In" value={formatTime(att?.check_in)} icon={<LogIn size={18} />} hint="Punch In" />
        <StatCard title="Check Out" value={formatTime(att?.check_out)} icon={<LogOut size={18} />} hint="Punch Out" />
        <StatCard title="Work Time" value={formatDuration(att?.total_minutes)} icon={<Clock size={18} />} hint="Net Time" />
        <StatCard title="Break Time" value={formatDuration(att?.break_minutes)} icon={<Coffee size={18} />} hint="Total Break" />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-4 mb-10">
        {!att?.check_in ? (
          <PrimaryButton
            label="Check In"
            icon={<LogIn size={16} />}
            onClick={checkIn}
            disabled={actionLoading || !canPunch}
          />
        ) : !att?.check_out ? (
          <PrimaryButton
            label="Check Out"
            icon={<LogOut size={16} />}
            variant="danger"
            onClick={checkOut}
            disabled={actionLoading || !canPunch}
          />
        ) : (
          <button
            className="px-5 py-2.5 bg-slate-200 text-slate-500 rounded-lg text-sm font-bold cursor-not-allowed"
            disabled
          >
            Day Finished
          </button>
        )}

        <button
          onClick={att?.break_active ? breakEnd : breakStart}
          disabled={actionLoading || !att?.check_in || att?.check_out}
          className={`px-5 py-2.5 rounded-lg text-sm font-bold flex items-center gap-2 ${
            att?.break_active
              ? "bg-amber-500 text-white"
              : "bg-blue-600 text-white disabled:bg-slate-200"
          }`}
        >
          <Coffee size={16} /> {att?.break_active ? "End Break" : "Start Break"}
        </button>
      </div>

      {/* History Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <h2 className="font-bold text-slate-800">Recent Activity</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 font-bold">
              <tr>
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">In</th>
                <th className="px-5 py-3">Out</th>
                <th className="px-5 py-3">Work</th>
                <th className="px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-slate-100">
              {history.length > 0 ? (
                history.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-4 font-medium text-slate-700">
                      {new Date(row.date).toLocaleDateString("en-IN")}
                    </td>
                    <td className="px-5 py-4 text-slate-600">{formatTime(row.check_in)}</td>
                    <td className="px-5 py-4 text-slate-600">{formatTime(row.check_out)}</td>
                    <td className="px-5 py-4 text-slate-600">{formatDuration(row.total_minutes)}</td>
                    <td className="px-5 py-4">
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          row.check_out ? "bg-blue-50 text-blue-600" : "bg-amber-50 text-amber-600"
                        }`}
                      >
                        {row.check_out ? "DONE" : "PENDING"}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-5 py-10 text-center text-slate-400">
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, hint }) {
  return (
    <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
          {title}
        </p>
        <span className="text-slate-400">{icon}</span>
      </div>
      <h2 className="text-lg font-bold text-slate-900">{value}</h2>
      <p className="text-[10px] text-slate-400">{hint}</p>
    </div>
  );
}

function PrimaryButton({ label, icon, onClick, disabled, variant = "primary" }) {
  const theme =
    variant === "danger" ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${theme} text-white px-5 py-2.5 rounded-lg text-sm font-bold flex items-center gap-2 shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all`}
    >
      {icon} {label}
    </button>
  );
}
